import { useCallback, useEffect, useState } from 'react';

import { useIsUnmounted } from '@altirllc/shared/ui/hooks/useIsUnmounted';

import { getUserInfo, hasStations } from '@/services/profile';

import { LOAD_SMARTLOOK_SCRIPTS } from '@/constants';
import { onAuthChange } from '@/utils/authUtils';
import { handleError } from '@/utils/errorUtils';
import { logger } from '@/utils/logger';

import { Loader } from '@/components/Loader';

import type { TUser } from '@/types/profile';

import { identifySmartlook } from '@/scripts/smartlook';
import { parseToken, tokenUtils } from '@/tokenUtils';
import { identifyTrackerUser } from '@/tracking';
import { resetTrackerUser } from '@/tracking/initTracking';

import type { TProviderProps } from '../types';
import { AuthContext, TAuthState } from './AuthContext';

export const AuthProvider = ({ children }: TProviderProps) => {
  const [state, setState] = useState<TAuthState | null>(null);

  const isUnmounted = useIsUnmounted();

  const onLogOut = useCallback(() => {
    resetTrackerUser();
    logger.resetUser();

    if (!isUnmounted.current) {
      setState({ authenticated: false });
    }
  }, [isUnmounted]);

  const tryLogIn = useCallback<() => void>(async () => {
    const tokens = tokenUtils.getLocallyStoredToken();

    if (!tokens) {
      return onLogOut();
    }

    const { userId } = parseToken(tokens);

    identifyTrackerUser(userId);
    logger.identifyUser(userId);

    try {
      const [user, userHasStations] = await Promise.all([
        getUserInfo().then(({ data }) => data.data),
        hasStations().then(({ data }) => data.data),
      ]);

      if (!isUnmounted.current) {
        setState({
          authenticated: true,
          hasStations: userHasStations,
          profileComplete: user.isProfileFillupComplete,
          profile: user,
        });

        if (LOAD_SMARTLOOK_SCRIPTS) {
          identifySmartlook(String(user.userId), { email: String(user.email) });
        }
      }
    } catch (error) {
      handleError(error, 'UNHANDLED_RESPONSE');

      tokenUtils.cleanTokens();
      onLogOut();
    }
  }, [isUnmounted, onLogOut]);

  const updateProfile = useCallback((profile: TUser) => {
    setState((currentState) => {
      if (currentState?.authenticated) {
        return {
          ...currentState,
          profileComplete: profile.isProfileFillupComplete,
          profile,
        };
      }

      return currentState;
    });
  }, []);

  // Keeps `hasStations` true after the Add Station wizard created a station (the value was read once
  // at sign-in). Same shape as `updateProfile`: only changes an authenticated state.
  const markHasStations = useCallback(() => {
    setState((currentState) => {
      if (currentState?.authenticated && !currentState.hasStations) {
        return { ...currentState, hasStations: true };
      }

      return currentState;
    });
  }, []);

  useEffect(() => tryLogIn(), [tryLogIn]);

  useEffect(() => onAuthChange(() => tryLogIn()), [tryLogIn]);

  return (
    <>
      {state && (
        <AuthContext.Provider value={{ ...state, updateProfile, markHasStations }}>
          {children}
        </AuthContext.Provider>
      )}

      {/* A saved sign-in waits on the profile call. Use the portal page color, not the white wash. */}
      <Loader active={!state} matchPage={Boolean(tokenUtils.getLocallyStoredToken())} />
    </>
  );
};
